import { useCallback } from 'react';
import { useTracking } from 'react-tracking';
import { Button } from '@simplywallst/ui-core';
import SortingOutline from '@simplywallst/ui-core/icons/SortingOutline';

import { Dropdown } from '@/components/Dropdown';
import type { ButtonRenderProps } from '@/components/Dropdown';

import { useHoldingsSorting } from '@/hooks/portfolio/useHoldingsSorting';
import type { HoldingsSorting } from '@/hooks/portfolio/useHoldingsSorting';

const sortingOptions: { option: HoldingsSorting; label: string }[] = [
  {
    option: 'performance-7d-desc',
    label: '7D Performance',
  },
  {
    option: 'value-desc',
    label: 'Total Value',
  },
  {
    option: 'cash-return-desc',
    label: 'Cash return',
  },
  {
    option: 'roi-desc',
    label: 'ROI',
  },
  {
    option: 'total-score-desc',
    label: 'Total score',
  },
  {
    option: 'value-score-desc',
    label: 'Value score',
  },
  {
    option: 'future-score-desc',
    label: 'Future score',
  },
  {
    option: 'past-score-desc',
    label: 'Past score',
  },
  {
    option: 'dividend-score-desc',
    label: 'Dividend score',
  },
  {
    option: 'health-score-desc',
    label: 'Health score',
  },
  {
    option: 'ticker-a-to-z',
    label: 'Ticker A - Z',
  },
  {
    option: 'company-name-a-to-z',
    label: 'Name A - Z',
  },
  {
    option: 'market-cap-desc',
    label: 'Market Cap',
  },
  {
    option: 'discount-desc',
    label: 'Most Undervalued',
  },
  {
    option: 'discount-asc',
    label: 'Most Overvalued',
  },
];

export const HoldingsSortingSelect = () => {
  const { trackEvent } = useTracking();

  const [value, setValue] = useHoldingsSorting();

  const renderButton = useCallback(
    ({ onBlur, onFocus, onClick, innerRef }: ButtonRenderProps) => {
      const sortingLabel =
        sortingOptions.find((o) => o.option === value)?.label || 'Sort';
      return (
        <Button
          ref={innerRef}
          onBlur={onBlur}
          onFocus={onFocus}
          onClick={onClick}
          styleType="passive"
          data-cy-id="holdings-sorting-select-button"
          alignIcon="left"
        >
          <SortingOutline aria-label="Sort Holdings" /> {sortingLabel}
        </Button>
      );
    },
    [value]
  );

  return (
    <Dropdown
      name="sortingSelection"
      align="right"
      value={value}
      options={sortingOptions}
      buttonRender={renderButton}
      onSelect={(o) => {
        setValue(o as HoldingsSorting);
        trackEvent({ modifier: 'select' });
      }}
    />
  );
};
